import React, { Fragment, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { ALL_USER_ROLES } from '@constants/roles/userRoleTypes';
import { exitImpersonation } from '@utils/miscellaneous/exitImpersonation';
import { environmentSelector } from '@selectors/commonSelectors';

// components

import Gears from './Gears';
import { GIButton, Modal } from 'app-components';

// hooks

import { useAppDispatch, useAppSelector, useBroker, useDisclosure } from '@hooks/index';

const BROKER_VIEW_PROFILE_URL = '/hix/broker/viewbrokerprofile';
const ASSISTER_VIEW_PROFILE_URL = '/hix/entity/assister/viewAssisterProfile';
const IMPERSONATION_EXIT_ROLES = new Set([
    ALL_USER_ROLES.BROKER,
    ALL_USER_ROLES.ASSISTER,
    ALL_USER_ROLES.ASSISTERENROLLMENTENTITY,
    ALL_USER_ROLES.APPROVEDADMINSTAFFL1,
    ALL_USER_ROLES.APPROVEDADMINSTAFFL2,
    ALL_USER_ROLES.AGENCY_MANAGER,
]);

const isValidSwitchBackUrl = link => {
    try {
        const { protocol } = new URL(link, window.location.origin);
        return ['http:', 'https:'].includes(protocol);
    } catch {
        return false;
    }
};
const isImpersonationExitRequired = (
    userActiveRole: string | undefined,
    switchBackLink: string | undefined,
): boolean => {
    if (!userActiveRole || !switchBackLink) return false;
    if (!isValidSwitchBackUrl(switchBackLink)) return false;
    return IMPERSONATION_EXIT_ROLES.has(userActiveRole);
};

function GearsContainer() {
    const dispatch = useAppDispatch();
    const { stateCode, user, impersonationDetails, enrollerDisplayBusinessPhone } = useAppSelector(environmentSelector);
    const userActiveModuleName = user.activeModuleName.toUpperCase();
    const userActiveRole = user.userActiveRole.toUpperCase();

    const { brokerProfile, dedesignateAssister, dedesignateBroker, getBrokerProfile } = useBroker();
    const { isOpen, onOpen, onClose } = useDisclosure();

    useEffect(() => {
        if (stateCode && userActiveModuleName) {
            getBrokerProfile(stateCode, userActiveModuleName);
        }
    }, [stateCode, userActiveModuleName]);

    const DesignateModal = (props) => {

        return (
            <Modal id="gearsModalContainer" data-testid="modal">
                <Modal.Header
                    id="gearsModalContainerHeader"
                    titleId={'gears.modal.title'}
                    onHide={props.onHide}
                    titleValues={{
                        firstName: props.firstName,
                        lastName: props.lastName,
                    }}
                />
                <Modal.Body>
                    <p>
                        <FormattedMessage
                            id="gears.modal.body"
                            values={{
                                firstName: props.firstName,
                                lastName: props.lastName,
                            }}
                        />
                    </p>
                </Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="id_btn_secondary"
                        labelId="button.cancel"
                        variant="secondary"
                        onClickHandler={props.onHide}
                    />{' '}
                    <GIButton
                        id="id_btn"
                        labelId="button.confirm"
                        onClickHandler={props.onConfirm}
                    />
                </Modal.Controls>
            </Modal>
        );
    };

    const onSelectViewProfile = () => {
        const { encryptedId, role } = brokerProfile;
        window.location.href = role === 'broker' ? `${BROKER_VIEW_PROFILE_URL}/${encryptedId}` : `${ASSISTER_VIEW_PROFILE_URL}/${encryptedId}`;
    };

    const handlePostDedesignation = () => {
        const switchBackLink = impersonationDetails?.switchBackLink;

        if (isImpersonationExitRequired(userActiveRole, switchBackLink)) {
            void exitImpersonation(dispatch, switchBackLink);
            return;
        }

        getBrokerProfile(stateCode, userActiveModuleName);
    };

    const onConfirm = async () => {
        const { encryptedId, role } = brokerProfile;
        const isBroker = role === 'broker';
        onClose();
        try {
            if (isBroker) {
                await dedesignateBroker(encryptedId);
            } else {
                await dedesignateAssister(encryptedId);
            }
            handlePostDedesignation();
        } catch (e) {
            const action = isBroker ? 'dedesignateBroker' : 'dedesignateAssister';
            console.error(`error in calling ${action} = `, e);
        }
    };

    if (!brokerProfile) {
        return null;
    }

    let brokerData = {};
    const {
        contactNumber,
        businessContactNumber,
        designateStatus,
        firstName,
        lastName,
        isShowGears,
        isShowDedesignateMsg,
        role
    } = brokerProfile;

    if (brokerProfile) {
        const isBroker = role === 'broker';
        const titleId = isBroker ? 'indportal.dashboard.gears.broker.title' : 'gears.assister.title';
        const pendingStatusMsgId = isBroker ? 'gears.broker.designation.request' : 'gears.assister.designation.request';
        const deDesignateMsgId = isBroker ? 'gears.broker.dedesignate.title' : 'gears.assister.dedesignate.title';

        const title = <FormattedMessage id={titleId} />;
        const viewProfileTitle = isBroker ? <FormattedMessage id="common.viewBrokerProfile" /> : <FormattedMessage id="common.viewProfile" />;
        const pendingStatusMsg = designateStatus === 'Pending' ? <FormattedMessage id={pendingStatusMsgId} /> : null;
        const currentStatusPending = designateStatus === 'Pending' ? <FormattedMessage id={'designation.status.pending'} /> : null;
        const deDesignateMsg = isShowDedesignateMsg ? <FormattedMessage id={deDesignateMsgId} /> : null;
        const viewProfileUrl = '';
        const deDesignateUrl = '';
        const phoneNumber = enrollerDisplayBusinessPhone ? businessContactNumber : contactNumber;

        brokerData = {
            name: `${firstName} ${lastName}`,
            phoneNumber,
            title,
            pendingStatusMsg,
            currentStatusPending,
            deDesignateMsg,
            viewProfileUrl,
            deDesignateUrl,
            viewProfileTitle,
        };
    }

    return (
        <Fragment>
            {
                isShowGears &&
                <Gears {...{ brokerData, onSelectViewProfile, onSelectDedesignateAgent: onOpen }} />
            }
            {
                isOpen && brokerProfile &&
                <DesignateModal {...{ show: isOpen, firstName: brokerProfile.firstName, lastName: brokerProfile.lastName, onHide:  onClose, onConfirm }} />
            }
        </Fragment>
    );
}

export default GearsContainer;
